import {
    Box,
    Button,
    ButtonRow,
    FormParagraph,
    FormSection,
    Icon,
    List,
    Modal,
    Text,
    Tooltip,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEmployment, updateEmployment } from '@frontend/services/api/employmentsApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IReduxState } from '../../../../../../../configureStore';
import { HolterLeadCount } from '../../../../../../../../common/enums/holterLeadCount';
import { RecorderType } from '../../../../../../../../common/enums/recorderType';
import { HolterInstructionSection } from './HolterInstructionSection';

type HolterConfiguredModalProps = {
    holterDiaryUrl: string;
    onClose: () => Promise<void>;
    recorderType: RecorderType;
};

export function HolterInstructionModal(props: Readonly<HolterConfiguredModalProps>) {
    const [leadCount, setLeadCount] = useState<HolterLeadCount | null>(null);
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const abortSignal = useAbortSignal();

    useEffect(() => {
        if (props.recorderType === RecorderType.bittium) {
            loadEmployment();
        }
    }, []);

    let title = '';
    if (props.recorderType === RecorderType.fysiologic) {
        title = 'Plakinstructies witte holter';
    } else if (props.recorderType === RecorderType.bittium && leadCount === HolterLeadCount.threeLeads) {
        title = 'Plakinstructies 3-leads holter';
    } else if (props.recorderType === RecorderType.bittium && leadCount === HolterLeadCount.fiveLeads) {
        title = 'Plakinstructies 5-leads holter';
    }

    return (
        <Modal
            isOpen
            onClose={async () => await props.onClose()}
            size="large"
        >
            <Modal.Header>{title}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                {props.recorderType === RecorderType.bittium && (
                    <FormSection title="Kies het aantal plakkers aan de recorder">
                        <ButtonRow>
                            <Button
                                onClick={async () => await saveEmployment(HolterLeadCount.threeLeads)}
                                variant={leadCount !== HolterLeadCount.threeLeads ? 'outline' : 'solid'}
                            >
                                3 plakkers
                            </Button>
                            <Button
                                onClick={async () => await saveEmployment(HolterLeadCount.fiveLeads)}
                                variant={leadCount !== HolterLeadCount.fiveLeads ? 'outline' : 'solid'}
                            >
                                5 plakkers
                            </Button>
                        </ButtonRow>
                    </FormSection>
                )}
                <FormSection title="Dagboek printen">
                    <FormParagraph>
                        Geef het dagboek mee en geef instructie over de frequentie en de mate van detail die nodig is
                        voor de verslaglegging
                    </FormParagraph>
                    <Button
                        onClick={() => window.open(props.holterDiaryUrl, '_blank')}
                        sx={{ mt: 3 }}
                        variant="outline"
                    >
                        Print dagboek
                    </Button>
                </FormSection>
                <FormSection title="Algemene voorbereiding">
                    <List
                        size="small"
                        styleType="bullet"
                    >
                        <List.Item>
                            Dames dienen hun bh uit te doen tijdens het aanbrengen van de ECG recorder
                        </List.Item>
                        <List.Item>Scheer bij mannen eventueel borsthaar weg met een scheermesje</List.Item>
                        <List.Item>Schuur de huid op met schuurpapier en reinig/ontvet de huid met alcohol</List.Item>
                        <List.Item>
                            Voordat u de elektroden plaatst dient u de “knikken” uit het draad te strijken met uw
                            vingers
                        </List.Item>
                    </List>
                    <Box
                        alignItems="center"
                        display="flex"
                        mt={4}
                    >
                        <Text
                            fontWeight="bold"
                            size="sm"
                        >
                            De plakkers dienen na 72 uur vervangen te worden, bij voorkeur op de praktijk
                        </Text>
                        <Tooltip content="Is het niet mogelijk om op de praktijk de plakkers te vervangen, dan kan de patiënt het ook zelf doen. Markeer hiervoor de plekken van de plakker met watervaste stift.">
                            <Icon
                                color="primary"
                                icon="information"
                                sx={{ ml: 2 }}
                            />
                        </Tooltip>
                    </Box>
                </FormSection>

                {props.recorderType === RecorderType.bittium && leadCount === HolterLeadCount.threeLeads && (
                    <FormSection title="Elektroden plaatsen">
                        <HolterInstructionSection
                            count="1"
                            description={
                                <>
                                    <strong>Rode elektrode/recorder</strong> - Plaats op de derde ruimte tussen de
                                    ribben rechts naast het borstbeen (geteld vanaf het sleutelbeen naar beneden)
                                    <br />
                                    <br />
                                    <strong>Gele elektrode</strong> - Plaats op de derde ruimte tussen de ribben links
                                    naast het borstbeen (geteld vanaf het sleutelbeen naar beneden)
                                </>
                            }
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-3-leads-1.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-3-leads-2.png',
                                },
                            }}
                            label="Rode en gele elektrode"
                        />

                        <HolterInstructionSection
                            count="2"
                            description="Plaats links op de punt van de ribbenkast tussen twee ribben"
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-3-leads-3.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-3-leads-4.png',
                                },
                            }}
                            label="Groene elektrode"
                        />

                        <HolterInstructionSection
                            count="3"
                            description="Bijna klaar! Nadat de elektroden geplaatst zijn, moeten ze nog worden gefixeerd met tape. Dit is om bewegingsstoornissen te voorkomen."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-3-leads-5.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-vinkje.png',
                                },
                            }}
                            label="Fixeren van de elektroden met tape"
                        />
                    </FormSection>
                )}

                {props.recorderType === RecorderType.bittium && leadCount === HolterLeadCount.fiveLeads && (
                    <FormSection title="Elektroden plaatsen">
                        <HolterInstructionSection
                            count="1"
                            description={
                                <>
                                    <strong>Rode elektrode/recorder</strong> - Plaats onder het rechter sleutelbeen
                                    dichtbij de schouder, in de tweede intercostaal ruimte (ruimte tussen de 2e en 3e
                                    rib)
                                    <br />
                                    <br />
                                    <strong>Gele elektrode</strong> - Plaats onder het linker sleutelbeen dichtbij de
                                    schouder, in de tweede intercostaal ruimte (ruimte tussen de 2e en 3e rib)
                                    <br />
                                    <br />
                                    <strong>Groene elektrode</strong> - Plaats linksonder in de ruimte van de onderste
                                    twee ribben in de middenlijn van het linker sleutelbeen (voorste axillairlijn). Bij
                                    een grote lichaams- of borstomvang kan deze marker meer richting de voorkant van de
                                    buik geplaatst worden.
                                </>
                            }
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-1.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-2.png',
                                },
                            }}
                            label="Rode, gele en groene elektroden"
                        />

                        <HolterInstructionSection
                            count="2"
                            description="Plaats rechts naast het borstbeen in de vierde intercostaalruimte (ruimte tussen 4e en 5e rib)"
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-3.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-4.png',
                                },
                            }}
                            label="Grijze elektrode"
                        />

                        <HolterInstructionSection
                            count="3"
                            description="Bevestig de recorder onder het borstbeen in het midden van de buik. De plakkers zitten dan volgens bovenstaande foto."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-5.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-6.png',
                                },
                            }}
                            label="Recorder plaatsen"
                        />

                        <HolterInstructionSection
                            count="4"
                            description="Bijna klaar! Nadat de elektroden geplaatst zijn, moeten ze nog worden gefixeerd met tape. Dit is om bewegingsstoornissen te voorkomen."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-bittium-5-leads-7.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-vinkje.png',
                                },
                            }}
                            label="Fixeren van de elektroden met tape"
                        />
                    </FormSection>
                )}

                {props.recorderType === RecorderType.fysiologic && (
                    <FormSection title=" Elektroden plaatsen">
                        <HolterInstructionSection
                            count="1"
                            description="De twee elektroden die paarsgewijs aan elkaar verbonden zijn, dienen onder de aanhechting van het sleutelbeen geplaatst te worden. De aanhechting is te voelen als twee knobbels, met daartussenin de makkelijk voelbare inkeping aan de bovenzijde van het borstbeen."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-1.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-2.png',
                                },
                            }}
                            label="Referentie elektrode"
                        />

                        <HolterInstructionSection
                            count="2"
                            description="De plakker met V1 erop wordt in de rechter vierde intercostaalruimte geplakt (tussen 4e en 5e rib). Deze kan gevonden worden door de bovenkant van het borstbeen te zoeken, 2-5 cm naar beneden te gaan naar een richel in het bot. Als je naar de zijkant van het borstbeen gaat voel je de tweede rib. Hieronder bevindt zich een ruimte (tweede intercostaalruimte). Ga 2 ruimtes naar beneden om de vierde te vinden."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-3.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-4.png',
                                },
                            }}
                            label="V1 elektrode"
                        />

                        <HolterInstructionSection
                            count="3"
                            description="De plakker met V5 erop wordt in de linker vijfde intercostaalruimte geplakt (tussen 5e en 6e rib), onder de linkerborst (ook als de borsten hangend zijn of omvangrijk)"
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-5.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-6.png',
                                },
                            }}
                            label="V5 elektrode"
                        />

                        <HolterInstructionSection
                            count="4"
                            description="Bijna klaar! Nadat de elektroden geplaatst zijn, moeten ze nog worden gefixeerd met tape. Dit is om bewegingsstoornissen te voorkomen."
                            images={{
                                leftImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-fysiologic-7.png',
                                },
                                rightImage: {
                                    alt: 'Recorder plak instructie',
                                    src: '/assets/images/holter/holter-plak-instructie-vinkje.png',
                                },
                            }}
                            label="Fixeren van de elektroden met tape"
                        />
                    </FormSection>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    key="close"
                    onClick={async () => await props.onClose()}
                >
                    Sluit af
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function loadEmployment() {
        const result = await getEmployment(selectedEmploymentId, abortSignal);
        setLeadCount(result.holterLeadCount ?? HolterLeadCount.threeLeads);
    }

    async function saveEmployment(holterLeadCount: HolterLeadCount) {
        setLeadCount(holterLeadCount);
        await updateEmployment(selectedEmploymentId, { holterLeadCount }, abortSignal);
    }
}
